// ============================================================
// CourrierPage — LA BOÎTE DE RÉCEPTION
// ------------------------------------------------------------
// Charles, 28 août 2026 : « on doit créer une in-box dans le programme
// pour que le client ait accès à ces mails. En dessous des mails il
// faut une petite explication de l'IA qui dit comment il en est arrivé
// là. »
//
// Cette dernière phrase est la raison d'être de l'écran. On pourrait se
// contenter d'afficher les devis créés — c'est ce qui compte à la fin —
// mais alors le tri serait une boîte noire : on verrait quatre devis
// pour dix mails, sans jamais savoir ce qu'étaient les six autres.
//
// L'explication est donc au même niveau que le mail, pas repliée
// derrière un « détail ». C'est elle qui permet de dire « non, ça,
// c'était une demande » — et donc de corriger le tri.
// ============================================================

import { useCallback, useState } from 'react';
import { Link } from 'react-router';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Badge from '../../ui/Badge.jsx';
import Button from '../../ui/Button.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';
import { useToast } from '../../ui/Toast.jsx';
// Un seul formateur pour tout le front — voir web/garde-affichage.test.js.
// Il rend « — » sur l'absence, là où `toLocaleString` rendrait
// « Invalid Date » sur une date manquante.
import { formatDateTime } from '../../lib/format.js';
import './courrier.css';

/** Le statut, en mots, et le TON qui va avec. */
const ETATS = {
  recu: { tone: 'neutral', label: 'reçu' },
  analyse: { tone: 'neutral', label: 'analysé' },
  en_attente: { tone: 'warn', label: 'en attente' },
  devis_cree: { tone: 'ok', label: 'devis créé' },
  sans_suite: { tone: 'neutral', label: 'sans suite' },
  echec: { tone: 'danger', label: 'échec' },
};

/** Ce qu'une pièce jointe est, dans les mots de l'atelier. */
const NATURES = {
  plan: 'plan',
  bon_de_commande: 'bon de commande',
  nomenclature: 'nomenclature',
  devis_concurrent: 'devis',
  document_texte: 'document',
  image_decorative: 'image',
  autre: 'autre',
};

function Mail({ mail }) {
  const etat = ETATS[mail.statut] ?? { tone: 'neutral', label: mail.statut };
  const faits = mail.faits ?? {};
  const pj = faits.pieces_jointes ?? [];
  const ecartees = faits.ecartees ?? [];

  return (
    <article className="courrier__mail">
      <header className="row row--between">
        <div>
          <p className="courrier__sujet">{mail.sujet || '(sans objet)'}</p>
          <p className="field__hint">
            {/* ── QUI DEMANDE, ET QUI A ENVOYÉ ──
                Charles transfère beaucoup de mails : afficher le seul
                émetteur ferait croire que tous les devis viennent de
                lui. Quand les deux diffèrent, on montre les deux. */}
            {mail.transfert && mail.expediteur_origine ? (
              <>
                <strong>{mail.expediteur_origine}</strong>
                {' '}— transféré par {mail.expediteur}
              </>
            ) : (
              <strong>{mail.expediteur ?? 'expéditeur inconnu'}</strong>
            )}
            {' · '}{formatDateTime(mail.recu_at)}
          </p>
        </div>
        <span className="row">
          <Badge tone={etat.tone}>{etat.label}</Badge>
          {mail.devis_reference ? (
            <Link to={`/devis/${mail.devis_id}`} className="courrier__lien">
              {mail.devis_reference}
            </Link>
          ) : null}
        </span>
      </header>

      {/* ══════════════════════════════════════════════════════════
          L'EXPLICATION DE L'AGENT — demandée par Charles
          ══════════════════════════════════════════════════════════
          Elle n'est pas repliée : sans elle, quatre devis pour dix
          mails resteraient inexplicables, et le tri deviendrait une
          boîte noire qu'on ne peut pas corriger. */}
      {mail.raisonnement ? (
        <p className="courrier__raisonnement">
          {mail.raisonnement}
          {mail.confiance === 'basse' ? (
            <> <Badge tone="warn">peu sûr</Badge></>
          ) : null}
        </p>
      ) : null}

      {/* Ce que le mail ANNONÇAIT, et que le plan ne dit pas. */}
      {faits.quantite || faits.matiere || faits.delai || faits.numero_commande ? (
        <p className="courrier__faits">
          {faits.quantite ? <span><strong>{faits.quantite}</strong> pièce(s)</span> : null}
          {faits.matiere ? <span>matière <strong>{faits.matiere}</strong></span> : null}
          {faits.delai ? <span>délai <strong>{faits.delai}</strong></span> : null}
          {faits.numero_commande ? <span>commande <strong>{faits.numero_commande}</strong></span> : null}
        </p>
      ) : null}

      {pj.length || ecartees.length ? (
        <ul className="courrier__pj">
          {pj.map((p) => (
            <li key={p.nom}>
              <span className="courrier__pj-nom">{p.nom}</span>
              <span className="courrier__pj-nature">{NATURES[p.nature] ?? p.nature}</span>
            </li>
          ))}
          {/* Ce qui a été écarté AVANT l'agent, et pourquoi. Une pièce
              jointe absente sans raison est une pièce qu'on cherche
              pendant une demi-heure. */}
          {ecartees.map((e) => (
            <li key={e.nom} className="courrier__pj--ecartee">
              <span className="courrier__pj-nom">{e.nom}</span>
              <span className="courrier__pj-nature">écartée — {e.raison}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Une tentative de faire sortir l'agent de son rôle. Elle n'a eu
          aucun effet — le schéma de sortie est fermé — mais elle se
          MONTRE : c'est un mail à regarder de près. */}
      {faits.anomalie ? (
        <p className="courrier__anomalie">
          <Badge tone="danger">consigne détectée</Badge> {faits.anomalie}
        </p>
      ) : null}

      {mail.statut === 'en_attente' ? (
        <p className="field__hint">
          {mail.client_id
            ? 'Demande retenue, mais aucun plan exploitable : le devis attend un dessin.'
            : `Aucun client ne correspond à ${mail.expediteur_origine ?? mail.expediteur}. `
              + 'Créez-le dans Clients avec cette adresse, puis relevez à nouveau.'}
        </p>
      ) : null}

      {mail.erreur ? <p className="courrier__erreur">{mail.erreur}</p> : null}
    </article>
  );
}

export function CourrierPage() {
  const toast = useToast();
  const [releve, setReleve] = useState(false);
  const load = useCallback(() => api.get('/courrier'), []);
  const { data, error, loading, reload } = useResource(load);

  async function relever() {
    setReleve(true);
    try {
      const r = await api.post('/courrier/relever', {});
      if (r.eteint) {
        toast.error('La réception de courrier est éteinte (IMAP_ENABLED).');
      } else {
        toast.success(`${r.releves} mail(s) relevé(s), ${r.traites} traité(s).`);
        reload();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReleve(false);
    }
  }

  const mails = data?.mails ?? [];
  const reception = data?.reception ?? {};

  return (
    <Card>
      <CardHead title="Courrier">
        <Button variant="secondary" disabled={releve} onClick={relever}>
          {releve ? 'Relevé en cours…' : 'Relever maintenant'}
        </Button>
      </CardHead>
      <CardBody className="stack">
        <p className="field__hint">
          Les mails arrivés sur <strong>devis@mertensmec.be</strong>. Le programme lit,
          garde les plans et prépare un devis <strong>brouillon</strong> — il ne chiffre
          rien : la gamme se demande depuis le devis, une fois que vous avez dit comment
          vous usineriez la pièce.
          {reception.actif === false ? (
            <> <Badge tone="warn">relevé automatique éteint</Badge></>
          ) : null}
        </p>

        {loading ? <SkeletonRows /> : null}
        {error ? <ErrorState message={error} onRetry={reload} /> : null}
        {!loading && !error && !mails.length ? (
          <EmptyState
            titre="Aucun mail"
            texte="Rien n’est encore arrivé, ou le relevé n’a pas encore tourné."
          />
        ) : null}

        {mails.map((m) => <Mail key={m.id} mail={m} />)}
      </CardBody>
    </Card>
  );
}

export default CourrierPage;
